import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/plugins/counter.css";

import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import chairman from "../assets/chairman.png";
import delivered1 from "../assets/delivedred1.jpeg";
import delivered2 from "../assets/delivedred2.jpeg";
import delivedred3 from "../assets/delivered3.jpg";
import delivered4 from "../assets/delivered4.png";
import delivered5 from "../assets/delivered5.png";

import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import EngineeringRoundedIcon from "@mui/icons-material/EngineeringRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";

import "./Developer.css";
import openWhatsApp from "../utils/sendWhatsapp";

const trustCards = [
  {
    icon: <FactCheckRoundedIcon />,
    title: "Approvals & Documentation",
    desc: "We share approvals, NOCs, and booking terms clearly before you commit.",
  },
  {
    icon: <EngineeringRoundedIcon />,
    title: "Quality Construction",
    desc: "Material specs, finishing standards, and work milestones are defined upfront.",
  },
  {
    icon: <ShieldRoundedIcon />,
    title: "Buyer Protection",
    desc: "Transparent timeline updates and clear possession process with documented steps.",
  },
  {
    icon: <GroupsRoundedIcon />,
    title: "Client Trust",
    desc: "Repeat buyers and referrals are our biggest proof — we focus on delivery.",
  },
];

const projects = [
  {
    name: "MCB Bank",
    location: "Al Kabir Town Phase-2, Lahore",
    year: "2022",
    status: "Delivered",
    trustTag: "On-time handover",
    image: delivered2,
  },
  {
    name: "HB Plaza",
    location: "Al Kabir Town Phase-2, Lahore",
    year: "2023",
    status: "Delivered",
    trustTag: "High finishing standard",
    image: delivered1,
  },
  {
    name: "10 Marla Modern House",
    location: "Bahria Town, Lahore",
    year: "2020",
    status: "Delivered",
    trustTag: "Strong investment value",
    image: delivedred3,
  },
  {
    name: "5 Marla Modern House",
    location: "Johar Town, Lahore",
    year: "2019",
    status: "Delivered",
    trustTag: "Client referrals",
    image: delivered4,
  },
  {
    name: "10 Marla Modern House",
    location: "Bahria Town, Lahore",
    year: "2022",
    status: "Delivered",
    trustTag: "Client referrals",
    image: delivered5,
  },
];

const Developer = () => {
  const { ref, inView } = useInView({ threshold: 0.16, triggerOnce: true });

  const [isViewerOpen, setIsViewerOpen] = React.useState(false);
  const [viewerIndex, setViewerIndex] = React.useState(0);

  const slides = projects.map((p) => ({
    src: p.image,
    alt: p.name,
  }));

  return (
    <section className="devx" id="developer" ref={ref}>
      <div className="devx__container">
        {/* Header */}
        <motion.div
          className="devx__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="devx__kicker">DEVELOPER</div>
          <h2 className="devx__title">
            A developer you can verify, not just believe.
          </h2>
          <p className="devx__sub">
            Buyers don’t just want a nice building — they want proof. Here’s who
            we are, how we work, and why people trust us.
          </p>
        </motion.div>

        {/* Top grid: Chairman + Trust */}
        <div className="devx__topGrid">
          {/* Chairman card */}
          <motion.div
            className="devx__chairmanCard"
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.05 }}
          >
            <div className="devx__chairmanRow">
              <div className="devx__chairmanImgWrap">
                {/* ✅ TEMP image (replace later with your real chairman image locally) */}
                <img
                  className="devx__chairmanImg"
                  src={chairman}
                  alt="Chairman"
                />
              </div>

              <div className="devx__chairmanMeta">
                <div className="devx__chairmanName">
                  Hafiz Sajjad Anwar{" "}
                  <span className="devx__verified">
                    <VerifiedRoundedIcon /> Verified Profile
                  </span>
                </div>
                <div className="devx__chairmanRole">
                  Chairman • Hafiz Brothers Developers & Builders
                </div>

                <div className="devx__chairmanMsg">
                  “We don’t sell promises. We show documents, timelines, and
                  quality standards. If something can’t be verified, we don’t
                  claim it.”
                </div>

                {/* <div className="devx__signature">
                  — Chairman Signature
                </div> */}
              </div>
            </div>

            <div className="devx__credStrip">
              <div className="devx__cred">
                <div className="devx__credNum">25+ yrs</div>
                <div className="devx__credLabel">Experience</div>
              </div>
              <div className="devx__cred">
                <div className="devx__credNum">48</div>
                <div className="devx__credLabel">Projects Delivered</div>
              </div>
              <div className="devx__cred">
                <div className="devx__credNum">2000+</div>
                <div className="devx__credLabel">Units Handed Over</div>
              </div>
            </div>

            <div className="devx__docNote">
              <span className="devx__dot" />
              Documents you can request: approvals/NOCs, company profile,
              payment schedule, and construction specs.
            </div>

            <div className="devx__chairmanBtns">
              <button
                onClick={() => {
                  openWhatsApp();
                }}
                className="devx__btn devx__btn--primary"
                type="button"
              >
                Request Documents <ArrowOutwardRoundedIcon />
              </button>
              <button
                onClick={() => {
                  openWhatsApp();
                }}
                className="devx__btn devx__btn--ghost"
                type="button"
              >
                Schedule Meeting <ArrowOutwardRoundedIcon />
              </button>
            </div>
          </motion.div>

          {/* Trust cards */}
          <motion.div
            className="devx__trustGrid"
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12 }}
          >
            {trustCards.map((t, idx) => (
              <motion.div
                key={idx}
                className="devx__trustCard"
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.05 * idx }}
              >
                <div className="devx__trustIcon">{t.icon}</div>
                <div>
                  <div className="devx__trustTitle">{t.title}</div>
                  <div className="devx__trustDesc">{t.desc}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Previous Projects Slider */}
        <motion.div
          className="devx__projectsWrap"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.18 }}
        >
          <div className="devx__projectsHead">
            <div>
              <div className="devx__projectsKicker">PREVIOUS PROJECTS</div>
              <h3 className="devx__projectsTitle">
                Delivered work builds real credibility.
              </h3>
              <p className="devx__projectsSub">
                Sample showcase (temporary). Replace with your real projects +
                photos later.
              </p>
            </div>
          </div>

          <Swiper
            modules={[ Pagination, Autoplay]}
         
            pagination={{ clickable: true }}
            autoplay={{ delay: 2600, disableOnInteraction: false }}
            spaceBetween={12}
            slidesPerView={1}
            breakpoints={{
              720: { slidesPerView: 2 },
              1100: { slidesPerView: 3 },
            }}
            className="devx__swiper"
          >
            {projects.map((p, idx) => (
              <SwiperSlide key={idx}>
                <div className="devx__projectCard">
                  <div className="devx__projectImgWrap">
                    <img src={p.image} alt={p.name} className="devx__projectImg" />
                    <div className="devx__projectBadge">
                      <TaskAltRoundedIcon /> {p.status}
                    </div>
                         <button
                    type="button"
                    className="devx__viewBtn"
                    onClick={() => {
                      setViewerIndex(idx);
                      setIsViewerOpen(true);
                    }}
                    aria-label="View image"
                    title="View Full Image"
                  >
                    <VisibilityRoundedIcon />
                  </button>
                  </div>

                  <div className="devx__projectBody">
                    <div className="devx__projectName">{p.name}</div>

                    <div className="devx__projectMeta">
                      <span><LocationOnRoundedIcon /> {p.location}</span>
                      <span><CalendarMonthRoundedIcon /> {p.year}</span>
                    </div>

                    <div className="devx__projectTrust">
                      {p.trustTag}
                    </div>
                  </div>
                </div>
        
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </div>

      <Lightbox
        open={isViewerOpen}
        close={() => setIsViewerOpen(false)}
        index={viewerIndex}
        slides={slides}
        plugins={[Zoom, Fullscreen, Counter]}
        on={{
          view: ({ index }) => setViewerIndex(index),
        }}
        styles={{
          container: { backgroundColor: "rgba(10,14,25,0.92)" },
        }}
      />
    </section>
  );
};

export default Developer;
